import React from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Facebook, Instagram, Coffee } from "lucide-react";

const IMG_1 =
  "https://customer-assets.emergentagent.com/job_napoli-artigianalita/artifacts/bsc3nisr_CC%20on%20the%20road.png";
const IMG_2 =
  "https://customer-assets.emergentagent.com/job_napoli-artigianalita/artifacts/afqpdp26_CC%20on%20the%20road%202.png";

const CaffeOnTheRoad = () => {
  const { t } = useTranslation();
  const whyList = t("road.whyList", { returnObjects: true }) || [];

  return (
    <section
      id="road"
      className="relative overflow-hidden"
      style={{ background: "#fbf5e9" }}
      data-testid="on-the-road-section"
    >
      <div className="absolute inset-x-0 top-0 h-32 pointer-events-none overflow-hidden">
        <div className="max-w-7xl mx-auto relative h-full">
          {[
            { left: "5%", top: "20%", size: 14, rot: -25, color: "#c5371f" },
            { left: "12%", top: "55%", size: 10, rot: 15, color: "#3b2417" },
            { left: "82%", top: "30%", size: 12, rot: 40, color: "#c5371f" },
            { left: "92%", top: "65%", size: 16, rot: -10, color: "#3b2417" },
            { left: "45%", top: "15%", size: 9, rot: 60, color: "#c5371f" },
          ].map((b, i) => (
            <span
              key={i}
              className="absolute rounded-full"
              style={{
                left: b.left,
                top: b.top,
                width: b.size,
                height: b.size * 1.4,
                background: b.color,
                transform: `rotate(${b.rot}deg)`,
                opacity: 0.18,
              }}
            />
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 section-y relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          <div
            className="lg:col-span-5 relative rounded-3xl p-8 sm:p-10 lg:p-12 overflow-hidden flex flex-col"
            style={{
              background: "linear-gradient(155deg, #d1432a 0%, #c5371f 55%, #9c2914 100%)",
              color: "#fff5ec",
              boxShadow: "0 30px 60px -25px rgba(156,41,20,0.55)",
            }}
          >
            <div className="absolute top-6 right-8 flex items-center gap-2 opacity-90">
              <span className="block w-3 h-4 rounded-full rotate-12" style={{ background: "#fff5ec" }} />
              <span className="block w-2.5 h-3.5 rounded-full -rotate-12" style={{ background: "#fff5ec" }} />
              <span className="block w-2 h-3 rounded-full rotate-45" style={{ background: "#fff5ec" }} />
            </div>

            <div className="relative">
              <h3
                className="font-script text-white leading-none"
                style={{
                  fontFamily: '"Dancing Script", "Caveat", cursive',
                  fontSize: "clamp(2.6rem, 5vw, 4.4rem)",
                  fontWeight: 700,
                  textShadow: "0 4px 18px rgba(0,0,0,0.18)",
                  transform: "rotate(-3deg)",
                }}
              >
                {t("road.script")}
              </h3>
              <svg viewBox="0 0 220 16" className="block mt-1" style={{ width: "min(280px, 60%)" }} aria-hidden="true">
                <path d="M5 10 Q 50 1, 110 7 T 215 5" stroke="#fff5ec" strokeWidth="2.5" fill="none" strokeLinecap="round" />
              </svg>
            </div>

            <div className="mt-8 sm:mt-10">
              <span className="font-serif-display uppercase tracking-[0.3em] text-xs" style={{ color: "#ffd9c9" }}>
                {t("road.eyebrow")}
              </span>
              <h2 className="font-serif-display font-bold mt-2 leading-tight" style={{ fontSize: "clamp(1.7rem, 2.6vw, 2.4rem)", color: "#fff" }}>
                {t("road.title1")}
                <br />
                <em className="not-italic" style={{ color: "#fff5ec", fontWeight: 700 }}>
                  {t("road.title2")}
                </em>
              </h2>
            </div>

            <p
              className="mt-5 text-[15px] sm:text-base leading-relaxed"
              style={{ color: "#fff1e3" }}
              dangerouslySetInnerHTML={{ __html: t("road.desc") }}
            />

            <div
              className="mt-7 rounded-2xl p-5 backdrop-blur-sm"
              style={{ background: "rgba(255, 245, 236, 0.12)", border: "1px solid rgba(255, 245, 236, 0.25)" }}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center" style={{ background: "#fff5ec", color: "#c5371f" }}>
                  <MapPin size={16} />
                </div>
                <div className="text-sm leading-tight">
                  <p className="font-semibold uppercase tracking-wider text-[11px] opacity-80">{t("road.sede")}</p>
                  <p className="font-medium mt-0.5" style={{ color: "#fff" }}>{t("road.sedeAddr")}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <a href="tel:+393348122013" className="flex items-center gap-3 group focus-ring rounded-lg" data-testid="road-phone">
                  <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "#fff5ec", color: "#c5371f" }}>
                    <Phone size={15} />
                  </div>
                  <div className="text-sm leading-tight">
                    <p className="font-semibold uppercase tracking-wider text-[11px] opacity-80">{t("road.phone")}</p>
                    <p className="font-medium" style={{ color: "#fff" }}>{t("road.phoneVal")}</p>
                  </div>
                </a>
                <a href="mailto:caffecola1960@libero.it" className="flex items-center gap-3 group focus-ring rounded-lg" data-testid="road-email">
                  <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center transition-transform group-hover:scale-110" style={{ background: "#fff5ec", color: "#c5371f" }}>
                    <Mail size={15} />
                  </div>
                  <div className="text-sm leading-tight">
                    <p className="font-semibold uppercase tracking-wider text-[11px] opacity-80">{t("road.email")}</p>
                    <p className="font-medium break-all" style={{ color: "#fff" }}>{t("road.emailVal")}</p>
                  </div>
                </a>
              </div>

              <div className="mt-5 flex items-center gap-3">
                <span className="text-[11px] uppercase tracking-wider opacity-80">{t("road.follow")}</span>
                <a href="https://www.facebook.com/caffecola1960" target="_blank" rel="noopener noreferrer" aria-label="Facebook Caffè Cola" className="w-9 h-9 rounded-full flex items-center justify-center transition hover:scale-110" style={{ background: "#fff5ec", color: "#c5371f" }} data-testid="road-social-fb">
                  <Facebook size={15} />
                </a>
                <a href="https://www.instagram.com/caffecola1960" target="_blank" rel="noopener noreferrer" aria-label="Instagram Caffè Cola" className="w-9 h-9 rounded-full flex items-center justify-center transition hover:scale-110" style={{ background: "#fff5ec", color: "#c5371f" }} data-testid="road-social-ig">
                  <Instagram size={15} />
                </a>
              </div>
            </div>

            <a
              href="#contatti"
              className="mt-7 inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-semibold uppercase tracking-wider text-sm self-start transition hover:scale-[1.02]"
              style={{ background: "#fff5ec", color: "#c5371f" }}
              data-testid="road-cta"
            >
              <Coffee size={16} /> {t("road.cta")}
            </a>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-6 gap-4 sm:gap-5">
            <div className="sm:col-span-6 relative rounded-3xl overflow-hidden shadow-xl group">
              <img src={IMG_2} alt={t("road.img2Alt")} className="w-full h-72 sm:h-96 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(42,22,12,0.55) 100%)" }} />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider" style={{ background: "rgba(255,245,236,0.95)", color: "#c5371f" }}>
                  <Coffee size={12} /> {t("road.badgeTruck")}
                </span>
                <span className="font-serif-display text-white text-lg drop-shadow-md hidden sm:inline" style={{ fontStyle: "italic" }}>
                  {t("road.badgeEvents")}
                </span>
              </div>
            </div>

            <div className="sm:col-span-3 relative rounded-3xl overflow-hidden shadow-xl group">
              <img src={IMG_1} alt={t("road.img1Alt")} className="w-full h-56 sm:h-72 object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(42,22,12,0.55) 100%)" }} />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="font-serif-display text-white text-base sm:text-lg italic drop-shadow">
                  {t("road.styleCaption")}
                </span>
              </div>
            </div>

            <div
              className="sm:col-span-3 rounded-3xl p-6 sm:p-7 flex flex-col justify-between"
              style={{ background: "#2a160c", color: "#f5ead6", boxShadow: "0 24px 50px -28px rgba(42,22,12,0.45)" }}
            >
              <div>
                <span className="font-serif-display uppercase tracking-[0.3em] text-[10px]" style={{ color: "#c5371f" }}>
                  {t("road.whyEyebrow")}
                </span>
                <h4 className="font-serif-display font-bold mt-2" style={{ fontSize: "clamp(1.2rem, 1.7vw, 1.6rem)", lineHeight: 1.2 }}>
                  {t("road.whyTitle")}
                </h4>
              </div>
              <ul className="mt-5 space-y-2.5 text-sm">
                {whyList.map((it, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#c5371f" }} />
                    <span style={{ color: "#ecdcbd" }}>
                      <strong style={{ color: "#f5ead6" }}>{it.t}:</strong> {it.d}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaffeOnTheRoad;
